import { QueryClient, useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CurrencyCode, DataType, GlobalAccount, GlobalAccountRoleType, GUID, PersonOrg } from '@ndvr/auth-api';
import { GlobalAccountUpdateRequest, HouseholdType } from '@ndvr/customer-api';

import { forgetRecentSearchEntity } from '@/components/recent-search-store';
import { CustomerApi } from 'API';
import { isLocalDev } from 'env';
import { FetcherError, getHouseholdTypeLabel, logger } from 'utils';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';
import { PersonFormValues } from 'views/person/person-form-model';
import { createPAM } from 'views/person/person-form-utils';
import { Toast } from 'widgets';

import { useAuthContext } from '../auth/Auth';

interface PersonHouseholdLink {
    globalAccountGUID: GUID<GlobalAccount>;
    personOrgGUID: GUID<PersonOrg>;
}

interface PersonHouseholdLinkWithRole extends PersonHouseholdLink {
    roleType: GlobalAccountRoleType;
    roleLabel?: string;
}

// Single source of truth for the household list query keys. The query hooks and the
// invalidation helper below both consume this factory so the keys cannot drift apart —
// the original stale-list bug existed because the keys were duplicated by hand and a bare
// ['globalAccount', 'list'] invalidation silently failed to prefix-match the tenant-scoped
// keys (React Query matches keys by prefix from index 0, and a household always belongs to
// a tenant, so 'globalAccount' is buried behind the tenant GUIDs in the scoped keys).
export const HouseholdQueryKeys = {
    // useAllCommandCenterHouseholds — all available tenants
    allList: () => ['globalAccount', 'list'] as const,
    // useCommandCenterHouseholds — visible list, scoped to the selected tenants
    ccList: (tenantGUIDs: string[]) => ['tenants', tenantGUIDs, 'globalAccount', 'cc-list'] as const,
    // useHouseholdsBySelectedTenants — GUID->household map, scoped to the selected tenants
    map: (tenantGUIDs: string[]) => ['tenants', tenantGUIDs, 'globalAccount', 'map'] as const,
} as const;

// Invalidate every household list view after a mutation. tenantGUIDs must be the selected
// tenants (useSelectedTenantGUIDs) so the scoped keys prefix-match the live queries.
const invalidateHouseholdLists = (queryClient: QueryClient, tenantGUIDs: string[]) => {
    queryClient.invalidateQueries({ queryKey: HouseholdQueryKeys.allList() });
    queryClient.invalidateQueries({ queryKey: HouseholdQueryKeys.ccList(tenantGUIDs) });
    queryClient.invalidateQueries({ queryKey: HouseholdQueryKeys.map(tenantGUIDs) });
};

export const useGlobalAccount = (
    globalAccountGUID?: GUID<GlobalAccount>,
    options?: Partial<UseQueryOptions<GlobalAccount | undefined>>,
) => {
    return useQuery({
        ...options,
        queryKey: ['globalAccount', globalAccountGUID],
        queryFn: async () => {
            const [response] = await CustomerApi().getGlobalAccount(globalAccountGUID!);
            return response ?? undefined;
        },
        enabled: !!globalAccountGUID,
    });
};

export const useCommandCenterHouseholds = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        queryKey: HouseholdQueryKeys.ccList(tenantGUIDs),
        queryFn: async () => {
            const [response] = await CustomerApi().getCommandCenterHouseholdListForTenants(tenantGUIDs);
            return response ?? [];
        },
        enabled: tenantGUIDs.length > 0,
    });
};

const useHouseholdsBySelectedTenants = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery<GlobalAccount[]>({
        queryKey: HouseholdQueryKeys.map(tenantGUIDs),
        queryFn: async () => {
            const [response] = await CustomerApi().getGlobalAccountsByTenants(tenantGUIDs);
            return response ?? [];
        },
        enabled: tenantGUIDs.length > 0,
    });
};

export const useHouseholdMap = () => {
    const { data } = useHouseholdsBySelectedTenants();
    return useMemo(
        () =>
            data?.reduce(
                (map, household) => map.set(household.globalAccountGUID, household),
                new Map<string, GlobalAccount>(),
            ) || new Map<string, GlobalAccount>(),
        [data],
    );
};

export const useAllCommandCenterHouseholds = () => {
    const { availableTenants } = useAuthContext();
    return useQuery<GlobalAccount[]>({
        queryKey: HouseholdQueryKeys.allList(),
        queryFn: async () => {
            const [response] = await CustomerApi().getGlobalAccountsByTenants(
                availableTenants.map((tenant) => tenant.tenantGUID),
            );
            return response ?? [];
        },
        enabled: availableTenants.length > 0,
    });
};

export const useAllHouseholdMap = () => {
    const { data } = useAllCommandCenterHouseholds();
    return useMemo(
        () =>
            data?.reduce(
                (map, household) => map.set(household.globalAccountGUID, household),
                new Map<string, GlobalAccount>(),
            ) || new Map<string, GlobalAccount>(),
        [data],
    );
};

export const useClientsAndProspects = () => {
    const { data: households, ...rest } = useCommandCenterHouseholds();
    return {
        ...rest,
        data: households?.filter((h) => h.householdType !== HouseholdType.INACTIVE_CLIENT) ?? [],
    };
};

export const useCommandCenterHouseholdProfile = (globalAccountGUID: string) => {
    return useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'commandCenterHouseholdProfile'],
        queryFn: async () => {
            if (globalAccountGUID) {
                const [response] = await CustomerApi().getCommandCenterHouseholdProfile(globalAccountGUID);
                return response ?? undefined;
            }
        },
    });
};

export const usePersonOrgLastLoginsForHousehold = (globalAccountGUID: string) =>
    useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'personOrgLastLogins'],
        queryFn: async () => {
            const [response] = await CustomerApi().getPersonOrgLastLoginsForHousehold(globalAccountGUID);
            return new Map((response ?? []).map(({ personOrgGUID, lastLoginAt }) => [personOrgGUID, lastLoginAt]));
        },
    });

export const useAddPersonToHouseholdWithRole = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: PersonHouseholdLinkWithRole) => {
            request.roleType === GlobalAccountRoleType.PRIMARY_ACCOUNT_MANAGER
                ? await CustomerApi().setPrimaryAccountManager(request.globalAccountGUID, {
                      personOrgGUID: request.personOrgGUID,
                  })
                : await CustomerApi().addGlobalAccountPersonOrgRole(request.globalAccountGUID, request.personOrgGUID, {
                      roleType: request.roleType,
                      roleLabel: request.roleLabel,
                  });
        },

        onSuccess: (_, data) => {
            Toast.success('Person linked to account');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', data.globalAccountGUID],
            });
        },
    });
};

export const useUnlinkPersonFromHousehold = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: PersonHouseholdLink) => {
            return CustomerApi({ hideError: true }).unlinkPersonFromAccount(
                request.globalAccountGUID,
                request.personOrgGUID,
            );
        },

        onSuccess: (_, data) => {
            Toast.success('Person unlinked from account');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', data.globalAccountGUID],
            });
        },
        onError: (error: any) => {
            Toast.forError(error, 'Failed to unlink person from account');
        },
    });
};

export const buildAdvisorLoginUrl = ({
    appBaseUrl,
    globalAccountGUID,
    loginToken,
    redirect,
}: {
    appBaseUrl: string;
    globalAccountGUID: string;
    loginToken: string;
    redirect?: string;
}) => {
    const query = new URLSearchParams({
        globalAccountGUID,
        token: loginToken,
    });

    if (redirect) {
        query.set('redirect', redirect);
    }

    return `${appBaseUrl}/login?${query.toString()}`;
};

export const sanitizeAdvisorLoginUrlForLogging = (loginUrl: string) => {
    const url = new URL(loginUrl);
    url.searchParams.delete('token');
    return url.toString();
};

export const useAdvisorLogin = () =>
    useMutation({
        mutationFn: async ({ redirect, householdGUID }: { redirect?: string; householdGUID: string }) => {
            const [response] = await CustomerApi().loginAsAdvisorToHousehold(householdGUID);

            if (!response) {
                throw new Error('Invalid response');
            }

            const appBaseUrl = isLocalDev ? 'https://app.local.internal.ndvr.com:8080' : response.webBaseUrl;
            const loginUrl = buildAdvisorLoginUrl({
                appBaseUrl,
                globalAccountGUID: response.globalAccountGUID,
                loginToken: response.loginToken,
                redirect,
            });

            logger.info(`Navigating to client portal URL: ${sanitizeAdvisorLoginUrlForLogging(loginUrl)}`);

            // open webapp's link in new TAB
            window.open(loginUrl, '_blank');
        },
        onError: (error: any) => {
            Toast.forError(error, 'Failed to login');
        },
    });

type AccountCreationRequest =
    | {
          tenantGUID: string;
          personOrgGUID: string;
          person?: never;
          primaryAdvisorGUID?: string;
          householdType?: HouseholdType;
      }
    | {
          tenantGUID: string;
          personOrgGUID?: never;
          person: PersonFormValues;
          primaryAdvisorGUID?: string;
          householdType?: HouseholdType;
      };
export const useCreateGlobalAccount = () => {
    const queryClient = useQueryClient();
    const tenantGUIDs = useSelectedTenantGUIDs();
    return useMutation({
        mutationFn: async ({
            tenantGUID,
            personOrgGUID,
            primaryAdvisorGUID,
            person,
            householdType,
        }: AccountCreationRequest) => {
            if (!personOrgGUID && !person) {
                return;
            }
            const personGUID = personOrgGUID || (await createPAM(tenantGUID, person!))?.personOrgGUID;
            if (!personGUID) {
                throw new Error('GUID not found');
            }

            const [response] = await CustomerApi().createCommandCenterHousehold(
                tenantGUID,
                personGUID,
                {
                    defaultCurrencyCode: CurrencyCode.USD,
                    householdType,
                },
                // The advisor select submits '' for its empty option, which is not a valid GUID.
                { primaryAdvisorGUID: primaryAdvisorGUID || undefined },
            );

            return response;
        },

        onSuccess: (data) => {
            Toast.success(`${getHouseholdTypeLabel(data?.householdType ?? HouseholdType.CLIENT)} created.`);
            invalidateHouseholdLists(queryClient, tenantGUIDs);
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to create Household');
        },
    });
};

export const useUpdateGlobalAccount = (globalAccountGUID: string) => {
    const queryClient = useQueryClient();
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useMutation({
        mutationFn: async ({
            globalAccountNamePlain = '',
            ffEmployeePersonOrgGUID,
            ffAffiliationLevel,
            primaryAdvisorGUID,
            serviceCadence,
            householdType,
        }: GlobalAccountUpdateRequest) => {
            const [response] = await CustomerApi().updateGlobalAccount(globalAccountGUID, {
                defaultCurrencyCode: CurrencyCode.USD,
                globalAccountName: {
                    encryptionContextGUID: globalAccountGUID,
                    dataType: DataType.GLOBAL_ACCOUNT_NAME,
                    plainText: globalAccountNamePlain,
                },
                globalAccountNamePlain,
                // The advisor select submits '' for its empty option, which is not a valid GUID.
                primaryAdvisorGUID: primaryAdvisorGUID || undefined,
                ffEmployeePersonOrgGUID,
                ffAffiliationLevel,
                serviceCadence,
                householdType,
            });

            return response ?? undefined;
        },

        onSuccess: () => {
            Toast.success('Household updated');
            invalidateHouseholdLists(queryClient, tenantGUIDs);
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to update Household');
        },
    });
};

export const useConvertProspectToClient = () => {
    const queryClient = useQueryClient();
    const tenantGUIDs = useSelectedTenantGUIDs();
    return useMutation({
        mutationFn: async (householdGUID: GUID<GlobalAccount>) => {
            await CustomerApi().convertProspectToClient(householdGUID);
        },
        onSuccess: (_, householdGUID) => {
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
            });
            invalidateHouseholdLists(queryClient, tenantGUIDs);
        },
        onError: (error) => Toast.forError(error, 'Failed to convert prospect to client'),
    });
};

export const useConvertClientToProspect = () => {
    const queryClient = useQueryClient();
    const tenantGUIDs = useSelectedTenantGUIDs();
    return useMutation({
        mutationFn: async (householdGUID: GUID<GlobalAccount>) => {
            await CustomerApi().convertClientToProspect(householdGUID);
        },
        onSuccess: (_, householdGUID) => {
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
            });
            invalidateHouseholdLists(queryClient, tenantGUIDs);
        },
        onError: (error) => Toast.forError(error, 'Failed to convert household to prospect'),
    });
};

export const useDeleteHousehold = () => {
    const queryClient = useQueryClient();
    const tenantGUIDs = useSelectedTenantGUIDs();
    return useMutation({
        mutationFn: async (householdGUID: GUID<GlobalAccount>) => {
            const [response] = await CustomerApi({ hideError: true }).deleteGlobalAccount(householdGUID);
            return response ?? undefined;
        },
        onSuccess: (result, householdGUID) => {
            invalidateHouseholdLists(queryClient, tenantGUIDs);
            if (result?.valid === true) {
                forgetRecentSearchEntity(householdGUID);
            }
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
                refetchType: result?.valid === true ? 'none' : 'active',
            });
        },
        onError: (error, householdGUID) => {
            if (error instanceof FetcherError && error.httpStatus === 404) {
                invalidateHouseholdLists(queryClient, tenantGUIDs);
                Toast.error('This household no longer exists', 'info');
                forgetRecentSearchEntity(householdGUID);
                queryClient.invalidateQueries({
                    queryKey: ['globalAccount', householdGUID, 'commandCenterHouseholdProfile'],
                });
                return;
            }
            Toast.forError(error, "Couldn't delete household");
        },
    });
};

export const usePersonOrgGlobalAccountRoles = (personOrgGUID?: GUID<PersonOrg>) =>
    useQuery({
        queryKey: ['person', personOrgGUID, 'globalAccountRoles'],
        queryFn: async () => {
            if (personOrgGUID) {
                const [response] = await CustomerApi().getAccountsByPerson(personOrgGUID);
                return response ?? undefined;
            }
        },
        enabled: !!personOrgGUID,
    });

export const usePrimaryAccountManager = (globalAccountGUID: GUID<GlobalAccount>) =>
    useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'primaryAccountManager'],
        queryFn: async () => {
            const [response] = await CustomerApi().getPrimaryAccountManager(globalAccountGUID);
            return response ?? undefined;
        },
        enabled: !!globalAccountGUID,
    });
